import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { stats } from "@/mocks/home";

export default function HeroSection() {
  const { t } = useTranslation();

  const scrollToSection = (href: string) => {
    const el = document.querySelector(href);
    if (el) {
      el.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <section
      id="home"
      className="relative min-h-[620px] md:min-h-[700px] flex items-center bg-foreground-950 overflow-hidden"
    >
      {/* Subtle geometric grid pattern */}
      <div className="absolute inset-0 opacity-[0.03]"
        style={{
          backgroundImage: `radial-gradient(circle at 1px 1px, white 1px, transparent 0)`,
          backgroundSize: "40px 40px",
        }}
      />

      {/* Subtle gradient orbs */}
      <div className="absolute top-[-20%] right-[-10%] w-[600px] h-[600px] rounded-full bg-primary-500/8 blur-[120px]" />
      <div className="absolute bottom-[-20%] left-[-5%] w-[400px] h-[400px] rounded-full bg-accent-500/6 blur-[100px]" />

      <div className="relative z-10 w-full px-6 lg:px-12 xl:px-20 pt-28 pb-16 md:pt-36 md:pb-24">
        <div className="max-w-4xl">
          <div className="inline-flex items-center gap-2.5 px-4 py-2 bg-white/5 border border-white/10 rounded-full mb-8">
            <span className="w-2 h-2 rounded-full bg-primary-500" />
            <span className="text-white/60 text-xs font-semibold tracking-widest uppercase">
              Enterprise Technology Solutions
            </span>
          </div>

          <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-extrabold text-white leading-[1.08] tracking-tight mb-6">
            {t("hero.title")}
            <br />
            <span className="text-primary-500">
              {t("hero.titleHighlight")}
            </span>
          </h1>

          <p className="text-base md:text-lg text-white/50 leading-relaxed max-w-2xl mb-10">
            {t("hero.subtitle")}
          </p>

          <div className="flex flex-col sm:flex-row gap-3 mb-16">
            <button
              onClick={() => scrollToSection("#contact")}
              className="px-8 py-3.5 bg-primary-500 hover:bg-primary-600 text-white font-semibold rounded-lg transition-all duration-200 text-sm whitespace-nowrap cursor-pointer inline-flex items-center justify-center gap-2"
            >
              {t("hero.ctaPrimary")}
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-right-line" />
              </div>
            </button>
            <button
              onClick={() => scrollToSection("#services")}
              className="px-8 py-3.5 border border-white/15 hover:border-white/30 text-white/70 hover:text-white font-medium rounded-lg transition-all duration-200 text-sm whitespace-nowrap cursor-pointer"
            >
              {t("hero.ctaSecondary")}
            </button>
          </div>

          {/* Stats row */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-white/8 rounded-xl overflow-hidden">
            {stats.map((stat) => (
              <div
                key={stat.label}
                className="bg-foreground-900 px-6 py-5 md:px-8 md:py-6"
              >
                <div className="text-2xl md:text-3xl font-bold text-white mb-1">
                  {stat.value}
                </div>
                <div className="text-xs md:text-sm text-white/40 font-medium">
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}